import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Users, Search } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { Card, CardContent } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import useDebouncedHandler from '../../../../hooks/useDebouncedHandler';
import AdminLayout from '../../../../layouts/AdminLayout';
import apiService from '../../../../services/api.service';
import DepartmentFilter from './components/DepartmentFilter';
import UsersTable from './components/UsersTable';

function AdminUsersPageV3({ title }) {
  const [usersData, setUsersData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [departments, setDepartments] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [departmentFilter, setDepartmentFilter] = useState([]);
  const history = useHistory();

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const departmentParam = window.location.search.slice(1);

      if (departmentParam && !departmentFilter.includes(departmentParam)) {
        setDepartmentFilter((prev) => [...prev, departmentParam]);
      }

      const sortValue = sortConfig.direction === 'asc' ? sortConfig.key : `-${sortConfig.key}`;

      const response = await apiService.admin.getUsers({
        params: {
          'filter[email]': usersSearch,
          'filter[departments]': departmentParam ? [departmentParam] : departmentFilter,
          skip: currentPage * rowsPerPage,
          limit: rowsPerPage,
          sort: sortValue,
        },
      });

      setUsersData(response.paginatedResult);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, rowsPerPage, sortConfig, usersSearch, departmentFilter]);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await apiService.admin.getDepartmentsLvls();
      setDepartments(response);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearchChange = (event) => {
    setUsersSearch(event.target.value.trim());
    setCurrentPage(0);
  };

  const { debouncedHandler } = useDebouncedHandler(handleSearchChange);

  const handleDepartmentFilterChange = (departmentId) => {
    setDepartmentFilter((prev) => {
      if (prev.includes(departmentId)) {
        return prev.filter((id) => id !== departmentId);
      }
      return [...prev, departmentId];
    });

    // Clear URL param if it matches the toggled department
    if (departmentId === window.location.search.slice(1)) {
      history.replace({ search: '' });
    }

    setCurrentPage(0);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
  };

  const handleUserUpdate = () => {
    fetchUsers();
  };

  return (
    <AdminLayout title={title}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{title}</h1>
              <p className="text-muted-foreground text-sm">
                Manage user accounts, roles, and permissions
              </p>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by email..."
              onChange={debouncedHandler}
              className="pl-9"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-6">
          {/* Department Filter Sidebar */}
          <DepartmentFilter
            departments={departments}
            selectedDepartments={departmentFilter}
            onFilterChange={handleDepartmentFilterChange}
          />

          {/* Users Table */}
          <Card className="flex-1" style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-0">
              <UsersTable
                users={usersData}
                departments={departments}
                isLoading={isLoading}
                totalCount={totalCount}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                sortConfig={sortConfig}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onUserUpdate={handleUserUpdate}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

AdminUsersPageV3.propTypes = {
  title: PropTypes.string.isRequired,
};

export default AdminUsersPageV3;
